import React, { useCallback, useEffect, useState } from 'react';
import {
  SystemAdminDataService,
  OrgMember,
  BreakGlassGrant,
  ADMIN_ROLE_ID,
} from '../../services/systemAdminDataService';

interface OrgAccessPanelProps {
  /** The organisation whose membership is shown. */
  orgId: number;
  /** Named in the grant confirmation, so the operator sees which org they're in. */
  orgName: string;
}

const fmtWhen = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/**
 * System Admin → Organisation → Access. Two jobs, in this order:
 *
 *   1. Answer "can anyone still administer this organisation?" — the question
 *      that brings an operator here, so the count leads and its zero state is
 *      the loudest thing on the page.
 *   2. Offer the break-glass grant, which is the only way back from zero.
 *
 * Everything else in the app protects this invariant from the inside: the org's
 * own admin screens refuse to remove or disable the last Admin. What they can't
 * cover is the org being stranded from outside — the sole admin leaves, or
 * loses their sign-in — and this panel is the answer to that, replacing what
 * used to need a hand-written statement against production.
 *
 * The grant is deliberately narrow: existing members only (a platform operator
 * must not be able to mint an identity inside a customer's organisation), never
 * External members, always with a reason that is kept.
 */
const OrgAccessPanel: React.FC<OrgAccessPanelProps> = ({ orgId, orgName }) => {
  const [users, setUsers] = useState<OrgMember[]>([]);
  const [grants, setGrants] = useState<BreakGlassGrant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The member the confirm dialog is about; null = dialog closed.
  const [target, setTarget] = useState<OrgMember | null>(null);
  const [reason, setReason] = useState('');
  const [granting, setGranting] = useState(false);
  const [grantError, setGrantError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await SystemAdminDataService.fetchOrgAccess(orgId);
      setUsers(data.users);
      setGrants(data.grants);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not load the members of this organisation.');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { void load(); }, [load]);

  const isAdmin = (u: OrgMember): boolean => u.roleIds.includes(ADMIN_ROLE_ID);
  const activeAdmins = users.filter(u => u.isActive && isAdmin(u));
  // Admins who hold the role but have no access — the shape of the problem
  // this tool exists for, and worth naming separately from "no admins at all".
  const disabledAdmins = users.filter(u => !u.isActive && isAdmin(u));
  const stranded = !loading && !error && activeAdmins.length === 0;

  const openGrant = (u: OrgMember) => {
    setTarget(u);
    setReason('');
    setGrantError(null);
    setDone(null);
  };

  const confirmGrant = async () => {
    if (!target) return;
    setGranting(true);
    setGrantError(null);
    try {
      const result = await SystemAdminDataService.grantOrgAdmin(orgId, target.id, reason);
      setTarget(null);
      setDone(
        result.reactivated
          ? `${result.name} is now an Admin, and their access to ${orgName} has been switched back on.`
          : `${result.name} is now an Admin of ${orgName}.`
      );
      await load();
    } catch (err: any) {
      setGrantError(err?.response?.data?.error || 'Could not grant the Admin role.');
    } finally {
      setGranting(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-success" role="status" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="sys-page__body" style={{ paddingTop: '1.25rem' }}>
        <section className="ivory-panel sys-panel sys-panel--pad">
          <div className="sys-notice">
            <i className="fas fa-triangle-exclamation" aria-hidden="true" />
            {error}
          </div>
        </section>
      </div>
    );
  }

  return (
    // Same wrapper as the Subscription tab: `.sys-page` itself has no padding,
    // so a panel dropped straight into it runs edge to edge and butts up
    // against the tab bar. The body supplies the gutters; the stack the gap.
    <div className="sys-page__body sys-stack" style={{ paddingTop: '1.25rem' }}>
      {/* ── The headline answer ─────────────────────────────────────────── */}
      <section className="ivory-panel sys-panel sys-panel--pad">
        <div className="sys-panel__head">
          <h2 className="sys-panel__title">
            <i className="fas fa-user-shield" aria-hidden="true" />Administrators
          </h2>
        </div>

        {stranded ? (
          <div className="sys-notice">
            <i className="fas fa-triangle-exclamation" aria-hidden="true" />
            <span>
              <strong className="sys-state sys-state--over">This organisation has no active Admins.</strong>{' '}
              Nobody here can manage users, children or settings. Grant the role below to restore it.
              {disabledAdmins.length > 0 && (
                <> {disabledAdmins.length === 1 ? 'One Admin has' : `${disabledAdmins.length} Admins have`}{' '}
                  been deactivated — granting the role to them switches their access back on.</>
              )}
            </span>
          </div>
        ) : (
          <div className="sys-notice">
            <i className="fas fa-circle-check" aria-hidden="true" />
            <span>
              <strong className="sys-state sys-state--on">
                {activeAdmins.length === 1 ? 'One active Admin' : `${activeAdmins.length} active Admins`}
              </strong>
              {': '}
              {activeAdmins.map(a => a.name).join(', ')}.
              {activeAdmins.length === 1 && ' The organisation cannot remove or deactivate this account from its own screens.'}
            </span>
          </div>
        )}

        {done && (
          <div className="sys-notice mt-2" role="status">
            <i className="fas fa-circle-check" aria-hidden="true" />{done}
          </div>
        )}
      </section>

      {/* ── Everyone in the organisation ───────────────────────────────── */}
      <section className="ivory-panel sys-panel">
        <div className="sys-panel__head sys-panel--pad">
          <h2 className="sys-panel__title">
            <i className="fas fa-users" aria-hidden="true" />Members
          </h2>
          <span className="sys-panel__hint">
            Grants take effect immediately and are recorded against your email.
          </span>
        </div>
        <div className="sys-table-scroll">
          <table className="sys-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Access</th>
                <th>Admin</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr><td colSpan={5} className="sys-table__empty">This organisation has no members.</td></tr>
              )}
              {users.map(u => (
                <tr key={u.id}>
                  <td className="sys-table__name">{u.name}</td>
                  <td className="sys-table__muted">{u.email}</td>
                  <td>
                    {u.isActive
                      ? <span className="sys-state sys-state--on"><i className="fas fa-circle-check" />Active</span>
                      : <span className="sys-state sys-state--off"><i className="fas fa-minus-circle" />Inactive</span>}
                    {u.isExternal && <span className="sys-tag ms-2">External</span>}
                  </td>
                  <td>
                    {isAdmin(u)
                      ? <span className="sys-state sys-state--on"><i className="fas fa-user-shield" />Admin</span>
                      : <span className="sys-table__muted">—</span>}
                  </td>
                  <td>
                    {/* Nothing to offer when they already administer the org and
                        have access. External members can never be Admins, so
                        say why rather than showing a button that would 400. */}
                    {u.isExternal ? (
                      <span className="sys-table__muted" title="External members are scoped to a child whitelist and cannot administer an organisation.">
                        Not eligible
                      </span>
                    ) : isAdmin(u) && u.isActive ? (
                      <span className="sys-table__muted">—</span>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-success"
                        onClick={() => openGrant(u)}
                      >
                        {isAdmin(u) ? 'Restore access' : 'Grant Admin'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── What's been done here before ───────────────────────────────── */}
      {grants.length > 0 && (
        <section className="ivory-panel sys-panel">
          <div className="sys-panel__head sys-panel--pad">
            <h2 className="sys-panel__title">
              <i className="fas fa-clock-rotate-left" aria-hidden="true" />Break-glass history
            </h2>
            <span className="sys-panel__hint">Every time a platform operator has reached into this organisation.</span>
          </div>
          <div className="sys-table-scroll">
            <table className="sys-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Promoted</th>
                  <th>By</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {grants.map(g => (
                  <tr key={g.id}>
                    <td className="sys-table__muted">{fmtWhen(g.grantedAt)}</td>
                    <td className="sys-table__name">
                      {g.userName}
                      {g.wasLastResort && (
                        <span className="sys-state sys-state--warn ms-2" title="The organisation had no active Admins at the time.">
                          recovery
                        </span>
                      )}
                    </td>
                    <td className="sys-table__muted">{g.grantedBy}</td>
                    <td>{g.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ── Confirm ─────────────────────────────────────────────────────
          A dialog rather than an inline button because this is a privileged
          cross-tenant action: it should cost a moment's thought, and the
          reason it asks for is kept permanently. */}
      {target && (
        <div className="modal show d-block" tabIndex={-1} role="dialog" style={{ background: 'rgba(13,77,26,0.35)' }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content ivory-panel">
              <div className="modal-header">
                <h5 className="modal-title">Grant the Admin role</h5>
              </div>
              <div className="modal-body">
                <p className="mb-3">
                  <strong>{target.name}</strong> ({target.email}) will be able to manage users, children,
                  settings and documents in <strong>{orgName}</strong>.
                  {!target.isActive && ' Their access to this organisation will also be switched back on.'}
                </p>

                <label className="form-label" htmlFor="bg-reason">Reason</label>
                <textarea
                  id="bg-reason"
                  className="form-control"
                  rows={3}
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="e.g. Support ticket 812 — sole admin left the setting, replacement confirmed by the manager."
                />
                <div className="form-text">
                  Kept permanently against this grant, with your email. At least 10 characters.
                </div>

                {grantError && (
                  <div className="alert alert-danger py-2 mt-3 mb-0">{grantError}</div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setTarget(null)}
                  disabled={granting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={confirmGrant}
                  disabled={granting || reason.trim().length < 10}
                >
                  {granting ? 'Granting…' : 'Grant Admin'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrgAccessPanel;
